import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
} from '@mui/material';
import { useApi } from '../hooks/useApi';

// F-206/F-207: the Company Checker consumes the canonical unified registry
// endpoint — the legacy `/company/search` chain no longer has a UI consumer.
interface RegistryResult {
  registryType: 'companies_house' | 'oscr';
  registrationNumber: string;
  name: string;
  status: string;
  registeredDate?: string;
  registeredAddress?: {
    line1?: string;
    line2?: string;
    city?: string;
    county?: string;
    postcode?: string;
    country?: string;
  };
  additionalInfo?: {
    companyType?: string;
    incorporationDate?: string;
  };
}

const registryLabel = (type: RegistryResult['registryType']) =>
  type === 'oscr' ? 'OSCR' : 'Companies House';

const CompanyChecker: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<RegistryResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const api = useApi();

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get<{ success: boolean; data?: RegistryResult[]; error?: string }>(
        `/company/registry/search?query=${encodeURIComponent(searchQuery)}`
      );
      const payload = response?.data;
      if (payload && payload.success === false) {
        setError(payload.error || 'Registry search failed');
        setResults([]);
      } else {
        setResults(payload?.data || []);
      }
    } catch (err) {
      setError('Failed to search registries. Please try again.');

    } finally {
      setLoading(false);
    }
  };

  const formatAddress = (address?: RegistryResult['registeredAddress']) => {
    if (!address) return 'No address available';

    return [
      address.line1,
      address.line2,
      address.city,
      address.postcode,
      address.country
    ].filter(Boolean).join(', ') || 'Address details not available';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-GB');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Company Checker
      </Typography>

      <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
        <TextField
          fullWidth
          label="Search by Company/Charity Name or Number"
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Search'}
        </Button>
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {results.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><Typography fontWeight="bold">Name</Typography></TableCell>
                <TableCell><Typography fontWeight="bold">Number</Typography></TableCell>
                <TableCell><Typography fontWeight="bold">Registry</Typography></TableCell>
                <TableCell><Typography fontWeight="bold">Status</Typography></TableCell>
                <TableCell><Typography fontWeight="bold">Type</Typography></TableCell>
                <TableCell><Typography fontWeight="bold">Registered Address</Typography></TableCell>
                <TableCell><Typography fontWeight="bold">Registered Date</Typography></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((entry) => (
                <TableRow key={`${entry.registryType}:${entry.registrationNumber}`}>
                  <TableCell>{entry.name}</TableCell>
                  <TableCell>{entry.registrationNumber}</TableCell>
                  <TableCell>
                    <Chip label={registryLabel(entry.registryType)} size="small" />
                  </TableCell>
                  <TableCell>{entry.status || 'Unknown'}</TableCell>
                  <TableCell>{entry.additionalInfo?.companyType || 'Unknown'}</TableCell>
                  <TableCell>{formatAddress(entry.registeredAddress)}</TableCell>
                  <TableCell>{formatDate(entry.registeredDate || entry.additionalInfo?.incorporationDate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {results.length === 0 && !loading && searchQuery && !error && (
        <Typography sx={{ textAlign: 'center', mt: 4 }}>
          No companies or charities found matching your search.
        </Typography>
      )}
    </Box>
  );
};

export default CompanyChecker;
